import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { useNavigate } from 'react-router';
import { KTIcon, KTSVG } from '../../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { formatDateFlatpickr, formatDateWithTime, formatDateWithoutTime } from '../../../../helper-functions/DateFunction';
import {
  delete_quotation,
  // get_all_quotations, // Will be replaced by get_filtered_quotations
  get_filtered_quotations
} from '../api/quotationsApi';
import { permissionCheck } from '../../../../helper-functions/PermissionCheck';
import Flatpickr from 'react-flatpickr';
import { get_all_requisitions as getAllRequisitionsForFilter } from '../../../requisitions/requisitions/api/requisitionsApi'; 
export type Quotation = {
    id: number;
    title: string | undefined;
    description: string | undefined;
    due_date: string;
    requisition_id: number | undefined;
    requisition_title: string | undefined;
    updated_by_name: string;
    updated_at: string;
};

type RequisitionForFilter = { 
    id: number;
    title: string | undefined;
};

type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiQuotationsDataPayload = {
    quotations: Quotation[]; // Changed from 'users' or 'products' or 'requisitions'
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiQuotationsFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiQuotationsDataPayload; // From your API structure
    };
};
// --- End Type Definitions ---


var quotation_add: boolean, quotation_edit: boolean, quotation_view: boolean, quotation_logs: boolean, quotation_delete: boolean;

function QuotationsList() {
    const [quotationList, setQuotationList] = useState<Quotation[]>([]); // For current page

    // Filter States
    const [searchInput, setSearchInput] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [requisitionId, setRequisitionId] = useState<string>(''); // Store as string for select

    // State for API filter payload
    const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
        requisitionId: '',
        fromDate: '',
        toDate: '',
        // searchTerm: '', // If backend supports search
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
    const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);

    // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // For server-side sorting

    const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
    
    const fromDateRef = useRef<Flatpickr>(null);
    const toDateRef = useRef<Flatpickr>(null);
    const [requisitionListForFilter, setRequisitionListForFilter] = useState<RequisitionForFilter[]>([]);
    
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    quotation_add = permissionCheck('quotation-create');
    quotation_edit = permissionCheck('quotation-update');
    quotation_view = permissionCheck('quotation-view');
    quotation_logs = permissionCheck('quotation-activity-log');
    quotation_delete = permissionCheck('quotation-delete');
    const { data: requisitionsForFilterData } = useQuery(
        'all_requisitions_for_filter', 
        () => getAllRequisitionsForFilter(), // Fetch a large number for dropdown, or adjust
        {
            refetchOnWindowFocus: false,
            onSuccess: (response) => {
                // Adjust based on actual response structure of getAllRequisitionsForFilter
                const requisitions = response?.data?.data?.requisitions || response?.data?.data || response?.data;
                if (requisitions && Array.isArray(requisitions)) {
                    setRequisitionListForFilter(requisitions as RequisitionForFilter[]);
                }
            }
        }
    );


    // --- Main Query for Filtered and Paginated Quotations ---
    const {
        // data: filteredQuotationsQueryData, // Full API response
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery<ApiQuotationsFullResponse, Error>(
        ['filtered_quotations', filterPayloadForApi, currentPage, itemsPerPage],
        () => get_filtered_quotations(
            filterPayloadForApi.requisitionId,
            filterPayloadForApi.fromDate,
            filterPayloadForApi.toDate,
            currentPage,
            itemsPerPage
            // filterPayloadForApi.searchTerm, // If server-side search
        ),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onSuccess: (response) => {
                const apiPayload = response.data.data;
                if (apiPayload && apiPayload.quotations && Array.isArray(apiPayload.quotations)) {
                    setQuotationList(apiPayload.quotations);
                    if (apiPayload.pagination) {
                        setTotalItems(apiPayload.pagination.total);
                        setTotalPages(apiPayload.pagination.last_page);
                        setCurrentPage(apiPayload.pagination.current_page);
                        setCurrentRangeFrom(apiPayload.pagination.from);
                        setCurrentRangeTo(apiPayload.pagination.to);
                    }
                } else {
                    setQuotationList([]);
                    setTotalItems(0);
                    setTotalPages(0);
                    setCurrentRangeFrom(1);
                    setCurrentRangeTo(0);
                }
            },
            onError: (error: any) => {
                toast.error(error.message || "Failed to fetch quotations.", { autoClose: 3000 });
                setQuotationList([]);
                setTotalItems(0);
                setTotalPages(0);
            },
        }
    );

    // Client-side search (optional)
    const displayedQuotationList = searchInput
        ? quotationList.filter((quot) =>
            quot.title?.toLowerCase().includes(searchInput.toLowerCase()) ||
            quot.requisition_title?.toLowerCase().includes(searchInput.toLowerCase())
          )
        : quotationList;


    // Mutation for deleting quotation
    const handleDeleteQuotation = useMutation(
        (id: number) => delete_quotation(id),
        {
            onSuccess: (response) => {
                toast.success(response.message || "Quotation deleted successfully.", { autoClose: 3000 });
                queryClient.invalidateQueries(['filtered_quotations', filterPayloadForApi, currentPage, itemsPerPage]);
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || error.message || "Failed to delete quotation.";
                toast.error(errorMessage, { autoClose: 3000 });
            },
        }
    );
  
    const handleApplyFilters = () => {
        setCurrentPage(1); // Reset to page 1
        setFilterPayloadForApi({
            requisitionId: requisitionId,
            fromDate: fromDate,
            toDate: toDate,
            // searchTerm: searchInput, // If server-side search
        });
    };

    const handleClearFilters = () => {
        if (fromDateRef.current) fromDateRef.current.flatpickr.clear();
        if (toDateRef.current) toDateRef.current.flatpickr.clear();
        setFromDate('');
        setToDate('');
        setRequisitionId('');
        setSearchInput('');

        setCurrentPage(1);
        setFilterPayloadForApi({
            requisitionId: '', fromDate: '', toDate: '', /* searchTerm: '' */
        });
    };

    // Date picker handlers
    const handleDateFromChange = (selectedDates: Date[]) => {
        if (selectedDates && selectedDates[0]) {
            const formatted = formatDateFlatpickr(selectedDates[0]);
            setFromDate(formatted);
            if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', selectedDates[0]);
        } else {
            setFromDate('');
            if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', null);
        }
    };
    const handleDateToChange = (selectedDates: Date[]) => {
        if (selectedDates && selectedDates[0]) {
            const formatted = formatDateFlatpickr(selectedDates[0]);
            setToDate(formatted);
        } else {
            setToDate('');
        }
    };

    // Pagination handlers
    const itemsPerPageOptions = [10, 20, 30, 50, 100];
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // Navigation handlers
    const handleQuotationLogs = (quot: Quotation) => navigate("/quotations/quotation-logs", { state: { id: quot.id } });
    const handleEditQuotation = (quot: Quotation) => navigate("/quotations/edit-quotation", { state: { quotation: quot } });
    const handleViewQuotation = (quot: Quotation) => navigate("/quotations/view-quotation", { state: { id: quot.id } });
    const handleManageDocuments = (quot: Quotation) => navigate("/quotations/quotation-document", { state: { id: quot.id } });

    // Removed client-side sorting. Server-side sorting would require backend changes.

    if (isLoading && !isFetching && currentPage === 1) { // Initial load
        return <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
    }

    return (
        <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
            {/* Card Header: Title and Add Button */}
            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-3 mb-1'>Quotations List</span>
                    <span className='text-muted mt-1 fw-semibold fs-7'>
                        {totalItems > 0 ? `${totalItems} quotations found` : 'No quotations'}
                    </span>
                </h3>
                <div className='card-toolbar'>
                    {quotation_add && (
                        <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/quotations/add-quotation')}>
                            <KTIcon iconName='plus' className='fs-3' /> Add Quotation
                        </button>
                    )}
                </div>
            </div>

            {/* Filters Section */}
            <div className='card-body pt-0'>
                <div className='row mb-4 align-items-center gy-3'>
                    <div className='col-md-3'>
                        <div className='input-group'>
                            <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
                            <input
                                type='text'
                                className='form-control form-control-solid'
                                placeholder='Search title, requisition...'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='col-md-3'>
                        <select
                            className='form-select form-select-solid'
                            value={requisitionId}
                            onChange={(e) => setRequisitionId(e.target.value)}
                        >
                            <option value=''>Requisition...</option>
                            {requisitionListForFilter.map((req) => (
                                <option key={req.id} value={req.id}>{req.title}</option>
                            ))}
                        </select>
                    </div>
                    <div className='col-md-2'>
                        <Flatpickr
                            ref={fromDateRef}
                            onChange={handleDateFromChange}
                            className='form-control form-control-solid'
                            placeholder='From Date (Due)'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                    <div className='col-md-2'>
                        <Flatpickr
                            ref={toDateRef}
                            onChange={handleDateToChange}
                            className='form-control form-control-solid'
                            placeholder='To Date (Due)'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                    <div className='col-md-2 d-flex justify-content-end align-items-center'>
                        <button className='btn btn-light-danger me-2' onClick={handleClearFilters} disabled={isFetching} style={{whiteSpace: 'nowrap'}}>
                            <KTIcon iconName='cross' className='fs-5 me-1' /> Clear
                        </button>
                        <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching} style={{whiteSpace: 'nowrap'}}>
                            <KTIcon iconName='filter-search' className='fs-5 me-1' /> Apply
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className='card-body py-3'>
                {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating quotation list...</i></div>}
                <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                            <tr className='fw-bold text-muted bg-light'>
                                <th className='ps-4 min-w-200px rounded-start'>Title</th>
                                <th className='min-w-200px'>Requisition Title</th>
                                <th className='min-w-120px'>Valid Until</th>
                                <th className='min-w-150px'>Updated By</th>
                                <th className='min-w-150px'>Updated At</th>
                                <th className='min-w-120px text-center'>Docs</th>
                                {(quotation_delete || quotation_edit || quotation_logs || quotation_view) &&
                                    <th className='min-w-150px text-center rounded-end'>Actions</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && currentPage === 1 ? (
                                <tr><td colSpan={7} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
                            ) : isError ? (
                                <tr><td colSpan={7} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
                            ) : displayedQuotationList.length > 0 ? (
                                displayedQuotationList.map((quot) => (
                                    <tr key={quot.id}>
                                        <td className='ps-4'>
                                            <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleViewQuotation(quot)}>
                                                {quot.title}
                                            </span>
                                            <span className='text-muted fw-semibold d-block fs-7'>
                                                {quot.description?.substring(0, 40) || ''}...
                                            </span>
                                        </td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{quot.requisition_title}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithoutTime(quot.due_date)}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{quot.updated_by_name}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(quot.updated_at)}</span></td>
                                        <td className='text-center'>
                                            <button onClick={() => handleManageDocuments(quot)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm' title="Manage Documents">
                                                <KTIcon iconName='file-up' className='fs-3' />
                                            </button>
                                        </td>
                                        {(quotation_delete || quotation_edit || quotation_logs || quotation_view) &&
                                        <td className='text-center'>
                                            <div className='d-flex justify-content-center flex-shrink-0'>
                                                {quotation_view && (<button onClick={() => handleViewQuotation(quot)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                                                {quotation_edit && (<button onClick={() => handleEditQuotation(quot)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                                                {quotation_logs && (<button onClick={() => handleQuotationLogs(quot)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                                                {quotation_delete && (<button onClick={() => setSelectedQuotation(quot)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_quotation'><KTIcon iconName='trash' className='fs-3' /></button>)}
                                            </div>
                                        </td>
                                        }
                                    </tr>
                                ))
                            ) : (
                                !isFetching && <tr><td colSpan={7} className="text-center py-5">No quotations found matching your criteria.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer: Pagination and Items Per Page */}
            {totalItems > 0 && (
                <div className='card-footer d-flex justify-content-between align-items-center py-4'>
                    <div>
                        <span className='text-muted me-2'>Showing:</span>
                        <select
                            className='form-select form-select-sm d-inline-block w-auto me-3'
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isFetching}
                        >
                            {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
                        </select>
                        <span className='text-muted'>
                            {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} quotations
                        </span>
                    </div>
                
                    <nav>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
                            </li>
                            {/* Optional: Add Page Number Logic */}
                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <div className="modal fade" tabIndex={-1} id="kt_modal_delete_quotation">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Quotation Confirmation</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete the quotation: <br/>
                                <strong>{selectedQuotation?.title}</strong>?
                            </p>
                            <p className='text-muted small'>This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => { if (selectedQuotation) handleDeleteQuotation.mutate(selectedQuotation.id as number); }} // Ensure ID is number
                                data-bs-dismiss="modal"
                                disabled={handleDeleteQuotation.isLoading}
                            >
                                {handleDeleteQuotation.isLoading ? 'Deleting...' : 'Delete Quotation'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuotationsList;